import React from "react";
import Grid from "@mui/material/Grid";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Tooltip from "@mui/material/Tooltip";
import StatBox from "../../../shared-components/StatBox";
import { useMediaQuery, Typography, useTheme, Box } from "@mui/material";
import {
  formatWeekDifference,
  getWeekDiffColor,
} from "../../../utils/formatters";

function DashboardStatBox(props) {
  return <StatBox cardVariant="shadow" {...props} />;
}

export default function StatsSummary({ stats, weeklyOnly }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const rows = buildRows(stats, isMobile);
  const period = weeklyOnly ? "week" : "month";

  return (
    <Grid container spacing={4}>
      {rows.map((row) => {
        const columnSize = calculateColumnSize(row.length);
        return row.map((stat) => {
          const rendered = renderStatContent(stat, period);
          return (
            <Grid key={stat.key} size={columnSize}>
              <DashboardStatBox title={stat.name} {...rendered} />
            </Grid>
          );
        });
      })}
    </Grid>
  );
}

function distributeStatsIntoRows(stats, maxPerRow = 4) {
  const rows = [];
  let remaining = stats.length;
  let index = 0;

  while (remaining > 0) {
    let rowSize = Math.min(maxPerRow, remaining);

    // Avoid lonely final card row like [4,1]
    if (remaining === maxPerRow + 1) {
      rowSize = maxPerRow - 1;
    }

    rows.push(stats.slice(index, index + rowSize));

    index += rowSize;
    remaining -= rowSize;
  }

  return rows;
}
function renderStatContent(stat, period) {
  const { type } = stat;

  switch (type) {
    case "change":
      return {
        stat: stat?.current ?? 0,
        footer: formatWeekDifference(stat),
        footerColor: getWeekDiffColor(stat),
      };

    case "progress":
      return {
        stat: stat.current,
        footer: (
          <ComparativeProgressBar
            total={stat.target}
            current={stat.current}
            relativeTotal={stat.relativeTarget}
            relativeName={`Last ${period}`}
          />
        ),
      };

    case "overall":
      if (stat.percentage) {
        return { stat: `${stat?.total ?? 0} %` };
      }
      return {
        stat: stat?.total ?? 0,
      };

    case "text":
      return {
        stat: stat?.text ?? "",
        textVariant: "body1",
      };

    case "status":
      return {
        stat: stat?.total ?? 0,
        footer: <Typography variant="caption">{stat?.status}</Typography>,
      };

    default:
      return {
        stat: "-",
        footer: "",
      };
  }
}

export function buildRows(stats, isMobile) {
  return isMobile ? stats.map((s) => [s]) : distributeStatsIntoRows(stats, 4);
}

export function calculateColumnSize(rowLength) {
  if (!rowLength) return 12;
  return Math.floor(12 / Math.min(rowLength, 4));
}

export const ComparativeProgressBar = ({
  current,
  total,
  relativeTotal,
  relativeName,
}) => {
  // Guard: we need the last period total to render a meaningful bar/tooltip.
  if (total == null || relativeTotal == null) return null;

  // Fill is progress towards last period max
  const fillRatio = total > 0 ? Math.min(current / total, 1) : 0;

  // Expected marker at elapsed fraction of total
  const markerLeftPct =
    total > 0
      ? Math.max(0, Math.min((relativeTotal ?? 0) / total, 1)) * 100
      : 0;

  // Ahead/behind compared to relative total
  const diffToDate = current - (relativeTotal ?? 0);
  const isAhead = diffToDate >= 0;

  // Percent difference relative to relative total
  const percentDifference =
    relativeTotal && relativeTotal > 0
      ? Math.round((Math.abs(diffToDate) / relativeTotal) * 100)
      : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        data-testid="progress-container"
      >
        <Tooltip title={`${relativeName} total: ${total}`} arrow>
          <Box
            sx={{
              position: "relative",
              flex: 1,
              height: 16,
              borderRadius: 8,
              backgroundColor: "action.hover",
              overflow: "hidden",
              cursor: "pointer",
            }}
            aria-label={`${relativeName} total: ${total}`}
          >
            {/* Actual progress fill */}
            <Box
              data-testid="progress-fill"
              sx={{
                height: "100%",
                width: `${fillRatio * 100}%`,
                backgroundColor: isAhead ? "success.main" : "warning.main",
                transition: "width 0.4s ease",
              }}
            />

            {/* Expected marker at elapsed fraction */}
            <Box
              data-testid="expected-marker"
              sx={{
                position: "absolute",
                left: `${markerLeftPct}%`,
                top: 0,
                bottom: 0,
                width: "2px",
                backgroundColor: "text.primary",
                transform: "translateX(-1px)",
              }}
            />
          </Box>
        </Tooltip>

        <Box display="flex" alignItems="center" gap={0.5}>
          {isAhead ? (
            <ArrowUpwardIcon
              data-testid="arrow-icon"
              data-direction="up"
              fontSize="small"
              sx={{ color: "success.main" }}
            />
          ) : (
            <ArrowDownwardIcon
              data-testid="arrow-icon"
              data-direction="down"
              fontSize="small"
              sx={{ color: "error.main" }}
            />
          )}

          <Typography
            variant="caption"
            sx={{
              color: isAhead ? "success.main" : "error.main",
              fontWeight: 600,
              minWidth: 45,
            }}
          >
            {percentDifference}%
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
